/**
 * Coordinate sanity for every map surface (pipeline review 2026-10-06).
 *
 * The live feed carries placeholder coordinates — (1.0, 1.0) rows and a
 * longitude of 111.0 — which dragged computed bounds and centres across
 * the globe (bbox [1.0, 1.0, 45.86, 45.83] for a Zagreb quarter).
 * Mirror of the backend `services/geo/sanitize.py`.
 */

// [minLat, minLng, maxLat, maxLng]
export const COUNTRY_BBOXES = {
  AT: [46.2, 9.3, 49.2, 17.4],
  HR: [42.1, 13.0, 46.8, 19.7],
  DE: [47.1, 5.7, 55.3, 15.2],
};

export function isUsableCoord(lat, lng) {
  if (typeof lat !== "number" || typeof lng !== "number") return false;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return false;
  // Null-Island placeholder
  return !(Math.abs(lat) < 0.01 && Math.abs(lng) < 0.01);
}

export function isInCountry(country, lat, lng) {
  if (!isUsableCoord(lat, lng)) return false;
  const box = COUNTRY_BBOXES[String(country || "").toUpperCase()];
  if (!box) return true;
  return lat >= box[0] && lat <= box[2] && lng >= box[1] && lng <= box[3];
}

/** Drop unusable coords, out-of-country rows and single far outliers. */
export function plausiblePoints(pts, country) {
  const base = (pts || []).filter((p) => isInCountry(country, p.lat, p.lng));
  if (base.length < 4) return base;
  const med = (arr) => {
    const s = [...arr].sort((a, b) => a - b);
    return s[Math.floor(s.length / 2)];
  };
  const mLat = med(base.map((p) => p.lat));
  const mLng = med(base.map((p) => p.lng));
  const W = 1.5; // ~150 km — a city/quarter result set never spans more
  const tight = base.filter(
    (p) => Math.abs(p.lat - mLat) <= W && Math.abs(p.lng - mLng) <= W,
  );
  return tight.length >= Math.max(3, base.length * 0.6) ? tight : base;
}

/** MapLibre bounds [[lngMin, latMin], [lngMax, latMax]] or null. */
export function boundsFromPoints(pts, minSpan = 0.02, pad = 0.05) {
  if (!pts || !pts.length) return null;
  let latMin = 90;
  let latMax = -90;
  let lngMin = 180;
  let lngMax = -180;
  pts.forEach((p) => {
    latMin = Math.min(latMin, p.lat);
    latMax = Math.max(latMax, p.lat);
    lngMin = Math.min(lngMin, p.lng);
    lngMax = Math.max(lngMax, p.lng);
  });
  if (latMax - latMin < minSpan) {
    latMin -= pad;
    latMax += pad;
  }
  if (lngMax - lngMin < minSpan) {
    lngMin -= pad;
    lngMax += pad;
  }
  return [[lngMin, latMin], [lngMax, latMax]];
}

/** Backend `[minLat, minLng, maxLat, maxLng]` → MapLibre bounds. */
export function boundsFromLocateBbox(bbox, pad = 0.01, country = null) {
  if (!Array.isArray(bbox) || bbox.length !== 4) return null;
  const [minLat, minLng, maxLat, maxLng] = bbox.map(Number);
  if (!isUsableCoord(minLat, minLng) || !isUsableCoord(maxLat, maxLng)) {
    return null;
  }
  // Degenerierte oder verdrehte Boxen sind kein Ausschnitt.
  if (!(maxLat > minLat) || !(maxLng > minLng)) return null;
  // Ein Ort/Viertel/Bezirk spannt nie ein halbes Erdteil. Die aus der
  // Produktion belegte Box [1.0, 1.0, 45.86, 45.83] entsteht, wenn eine
  // Platzhalter-Koordinate als Ecke durchrutscht — sie zoomt auf den
  // Indischen Ozean und muss verworfen werden, statt die Karte zu kippen.
  if (maxLat - minLat > 6 || maxLng - minLng > 8) return null;
  if (country
      && (!isInCountry(country, minLat, minLng)
          || !isInCountry(country, maxLat, maxLng))) {
    return null;
  }
  const dLat = maxLat - minLat < 0.02 ? pad + 0.02 : 0;
  const dLng = maxLng - minLng < 0.02 ? pad + 0.02 : 0;
  return [
    [minLng - dLng, minLat - dLat],
    [maxLng + dLng, maxLat + dLat],
  ];
}
